import type {
    Commerce,
    CommerceCartDomain,
    CommerceCheckoutDomain,
    CommerceContext,
    CommercePaymentAdapter,
    CommerceOperationMiddleware,
    CommerceShareProvider,
    CommerceEventListener,
    CommerceEventsDomain,
    CommerceMarketDomain,
    CommercePaymentDomain,
    CommerceOrderDomain,
    CommerceCustomerDomain,
    CommerceEnrollmentDomain,
    CommerceStorefrontDomain,
    CommerceShareDomain,
    CommerceProductCatalogue,
    CommerceRuntimeDiagnostics,
    CommerceSession,
    ResolvedCommerceConfiguration,
} from "@teqtank/commerce-contracts";
import type { CommerceTransport } from "@teqtank/commerce-transport";
import { CommerceCartRepository } from "../cart/commerce-cart-repository.js";
import { CommerceCartService } from "../cart/commerce-cart-service.js";
import { CommerceError } from "../errors/commerce-error.js";
import { CommerceCheckoutRepository } from "../checkout/commerce-checkout-repository.js";
import { CommerceCheckoutService } from "../checkout/commerce-checkout-service.js";
import { CommerceOperationExecutor } from "../execution/commerce-operation-executor.js";
import { CommerceLifecycleController } from "../lifecycle/commerce-lifecycle-controller.js";
import { CommerceProductCatalogueService } from "../products/commerce-product-catalogue-service.js";
import { ProductCatalogueRepository } from "../products/product-catalogue-repository.js";
import { CommercePaymentService } from "../payment/commerce-payment-service.js";
import { CommerceOrderRepository } from "../orders/commerce-order-repository.js";
import { CommerceOrderService } from "../orders/commerce-order-service.js";
import { CommerceCustomerRepository } from "../customer/commerce-customer-repository.js";
import { CommerceEnrollmentRepository } from "../enrollment/commerce-enrollment-repository.js";
import { CommerceEnrollmentService } from "../enrollment/commerce-enrollment-service.js";
import { CommerceCustomerService } from "../customer/commerce-customer-service.js";
import { CommerceStorefrontRepository } from "../storefront/commerce-storefront-repository.js";
import { CommerceStorefrontService } from "../storefront/commerce-storefront-service.js";
import { CommerceShareRepository } from "../share/commerce-share-repository.js";
import { CommerceShareService } from "../share/commerce-share-service.js";
import { CommerceEventHub } from "../events/commerce-event-hub.js";
import { CommerceMarketService } from "../context/commerce-market-service.js";

export class CommerceRuntime implements Commerce {
    public readonly lifecycle: CommerceLifecycleController;
    public readonly events: CommerceEventsDomain;
    public readonly market: CommerceMarketDomain;
    public readonly products: CommerceProductCatalogue;
    public readonly cart: CommerceCartDomain;
    public readonly checkout: CommerceCheckoutDomain;
    public readonly payment: CommercePaymentDomain;
    public readonly orders: CommerceOrderDomain;
    public readonly customer: CommerceCustomerDomain;
    public readonly enrollment: CommerceEnrollmentDomain;
    public readonly storefront: CommerceStorefrontDomain;
    public readonly share: CommerceShareDomain;
    private readonly cartService: CommerceCartService;
    private readonly checkoutService: CommerceCheckoutService;
    private readonly paymentService: CommercePaymentService;
    private readonly orderService: CommerceOrderService;
    private readonly customerService: CommerceCustomerService;
    private readonly enrollmentService: CommerceEnrollmentService;
    private readonly storefrontService: CommerceStorefrontService;
    private readonly shareService: CommerceShareService;
    private readonly checkoutJourney = { active: false };
    private readonly marketService: CommerceMarketService;
    private readonly eventHub: CommerceEventHub;
    private readonly contextState: { current: CommerceContext };
    private readonly eventUnsubscribers: Array<() => void> = [];

    public constructor(
        public readonly version: string,
        public readonly configuration: ResolvedCommerceConfiguration,
        public readonly session: CommerceSession,
        context: CommerceContext,
        public readonly diagnostics: CommerceRuntimeDiagnostics,
        transport: CommerceTransport,
        paymentAdapter?: CommercePaymentAdapter,
        operationMiddleware: readonly CommerceOperationMiddleware[] = [],
        shareProviders: readonly CommerceShareProvider[] = [],
        eventListeners: readonly CommerceEventListener[] = [],
    ) {
        this.lifecycle = new CommerceLifecycleController();
        this.contextState = { current: context };
        this.eventHub = new CommerceEventHub();
        this.events = this.eventHub;
        const executor = new CommerceOperationExecutor(
            transport,
            session,
            () => this.contextState.current,
            () => this.assertAvailable(),
            operationMiddleware,
        );
        this.products = new CommerceProductCatalogueService(
            new ProductCatalogueRepository(executor),
            configuration.productDetail,
        );
        this.cartService = new CommerceCartService(new CommerceCartRepository(executor));
        this.cart = this.cartService;
        this.checkoutService = new CommerceCheckoutService(
            new CommerceCheckoutRepository(executor),
            this.cartService,
        );
        this.checkout = this.checkoutService;
        this.customerService = new CommerceCustomerService(
            new CommerceCustomerRepository(executor),
            () => this.assertAvailable(),
        );
        this.customer = this.customerService;
        this.paymentService = new CommercePaymentService(
            () => this.checkoutService.current,
            (consents, customFields) => this.checkoutService.preparePayment(consents, customFields),
            async (attemptId, result) => {
                const completion = await this.checkoutService.completePayment(attemptId, result);
                this.checkoutJourney.active = false;
                this.checkoutService.invalidate();
                try {
                    await this.cartService.get();
                } catch {
                    this.cartService.invalidate();
                }
                return completion;
            },
            () => this.assertAvailable(),
            paymentAdapter,
        );
        this.payment = this.paymentService;
        this.orderService = new CommerceOrderService(new CommerceOrderRepository(executor), () =>
            this.assertAvailable(),
        );
        this.orders = this.orderService;
        this.enrollmentService = new CommerceEnrollmentService(
            new CommerceEnrollmentRepository(executor),
            () => this.assertAvailable(),
            async (enrollment) => {
                if (enrollment) {
                    this.cartService.adoptEnrollment(enrollment.cart, enrollment.updatedAt);
                    return;
                }
                await this.cartService.get();
            },
        );
        this.enrollment = this.enrollmentService;
        this.cartService.setMutationSynchronizer(async (cart) => {
            const checkoutWasActive = this.checkoutJourney.active;
            if (
                !this.enrollmentService.current &&
                this.checkoutService.current?.experience === "enrollment"
            ) {
                await this.enrollmentService.restoreIfAvailable();
            }
            await this.enrollmentService.refreshAfterCartChanged(cart);
            if (checkoutWasActive) {
                const checkout = await this.checkoutService.get();
                if (!checkout && cart.lines.length > 0) {
                    await this.checkoutService.beginForCart(cart);
                }
            }
        });
        this.eventUnsubscribers.push(
            this.checkoutService.onChanged((event) => {
                this.checkoutJourney.active = true;
                if (event.checkout.experience !== "enrollment" || this.enrollmentService.current) {
                    return;
                }

                void this.enrollmentService.restoreIfAvailable().catch(() => undefined);
            }),
        );
        this.customerService.setLogoutSynchronizer(async () => {
            this.products.clearCache();
            this.checkoutJourney.active = false;
            this.enrollmentService.clearAfterCustomerLogout();
            try {
                await this.cartService.get();
                await this.checkoutService.get();
                await this.enrollmentService.restoreIfAvailable();
            } catch {
                this.cartService.invalidate();
                this.checkoutService.invalidate();
                this.enrollmentService.invalidate();
            }
        });
        this.storefrontService = new CommerceStorefrontService(
            new CommerceStorefrontRepository(executor),
            context,
            () => this.assertAvailable(),
            configuration.storefront.resolveAlias,
        );
        this.storefront = this.storefrontService;
        this.marketService = new CommerceMarketService(
            executor,
            session,
            () => this.contextState.current,
            (nextContext) => {
                this.contextState.current = nextContext;
                this.products.clearCache();
                this.cartService.invalidate();
                this.checkoutJourney.active = false;
                this.checkoutService.invalidate();
                this.storefrontService.updateContext(nextContext);
            },
        );
        this.market = this.marketService;
        this.shareService = new CommerceShareService(
            new CommerceShareRepository(executor),
            () => this.storefrontService.current,
            () => this.cartService.current?.id ?? null,
            () => this.assertAvailable(),
            async (response) => {
                this.checkoutService.invalidate();
                this.enrollmentService.invalidate();
                const cart = this.cartService.adopt(response.cart);
                if (response.preview.experience === "enrollment") {
                    const enrollment = await this.enrollmentService.restoreIfAvailable();
                    if (!enrollment) {
                        this.cartService.invalidate();
                        throw new CommerceError(
                            "share-operation-failed",
                            "The shared enrollment could not be restored. Start enrollment again.",
                        );
                    }
                }
                return cart;
            },
        );
        this.share = this.shareService;
        for (const provider of shareProviders) this.shareService.registerProvider(provider);
        for (const listener of eventListeners)
            this.eventUnsubscribers.push(this.eventHub.on(listener));
        this.bridgeDomainEvents();
        Object.freeze(this);
    }

    public dispose(): void {
        if (this.lifecycle.isDisposed) return;
        this.products.clearCache();
        this.marketService.dispose();
        this.shareService.dispose();
        this.storefrontService.dispose();
        this.enrollmentService.dispose();
        this.customerService.dispose();
        this.orderService.dispose();
        this.paymentService.dispose();
        this.checkoutService.dispose();
        this.cartService.dispose();
        this.lifecycle.dispose();
        const disposedAt = new Date();
        this.eventHub.emit({
            name: "runtime.disposed",
            occurredAt: disposedAt,
            data: { disposedAt },
        });
        for (const unsubscribe of this.eventUnsubscribers.splice(0)) unsubscribe();
        this.eventHub.release();
    }

    private bridgeDomainEvents(): void {
        this.eventUnsubscribers.push(
            this.marketService.onChanged((data) =>
                this.eventHub.emit({
                    name: "context.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.cartService.onChanged((data) =>
                this.eventHub.emit({
                    name: "cart.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.checkoutService.onChanged((data) =>
                this.eventHub.emit({
                    name: "checkout.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.paymentService.onChanged((data) =>
                this.eventHub.emit({
                    name: "payment.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.orderService.onChanged((data) =>
                this.eventHub.emit({
                    name: "order.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.customerService.onChanged((data) =>
                this.eventHub.emit({
                    name: "customer.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.enrollmentService.onChanged((data) =>
                this.eventHub.emit({
                    name: "enrollment.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.storefrontService.onChanged((data) =>
                this.eventHub.emit({
                    name: "storefront.changed",
                    occurredAt: new Date(),
                    data,
                }),
            ),
            this.shareService.onEvent((data) =>
                this.eventHub.emit({
                    name: "share.event",
                    occurredAt: new Date(),
                    data,
                }),
            ),
        );
    }

    public get context(): CommerceContext {
        return this.contextState.current;
    }

    private assertAvailable(): void {
        if (this.lifecycle.isDisposed)
            throw new CommerceError("runtime-disposed", "The Commerce runtime has been disposed.");
    }
}
